import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { withTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { changeObjectPropertyAction, setPayDebtOnBehalf } from 'redux/actions'
import { initialContactInformation } from 'redux/reducers/appReducer'
import FieldInput from 'shared/components/FieldInput'
import { validateDateOfBirthErrors } from '../../../../../shared/helpers/validators'
import DateOfBirth from './DateOfBirth'
import PhoneNumber from 'shared/components/Contact/PhoneNumber'
import Email from 'shared/components/Contact/Email'
import { validateFullNameErrors, validatePhoneNumberErrors, validateEmailErrors } from 'shared/helpers/validators'
import { Fade } from 'shared/components/Fade'
import FullName from 'shared/components/Contact/FullName'
import { validateSocialInsuranceNumberErrors } from 'shared/helpers/validateSocialInsuranceNumber'
import styles from './ContactInformationForm.module.scss'

const REDUX_APP_PROPERTY = 'contactInformation'
const PAY_ON_BEHALF_PROPERTY = 'payeeContactInformation'

const initialPayeeFormErrors = { payeePhoneNumber: '', payeeFullName: '', payeeEmail: '' }

const ContactInformationForm = ({ t, i18n, editDebtInfoId, setValidateForm, setIsError }) => {
  const dispatch = useDispatch()
  const [formErrors, setFormErrors] = useState({ ...initialContactInformation })

  const [payeeFormErrors, setPayeeFormErrors] = useState(initialPayeeFormErrors)
  const dateOfBirthDayEl = useRef(null)
  const dateOfBirthMonthEl = useRef(null)
  const dateOfBirthYearEl = useRef(null)

  const { fullName, dateOfBirth, socialInsuranceNumber, phoneNumber, email } = useSelector((state) => state.app.contactInformation)
  const payeeContactInformation = useSelector((state) => state.app.payeeContactInformation)

  useEffect(() => {
    setPayeeFormErrors(initialPayeeFormErrors)
  }, [payeeContactInformation])

  const handleChangeObjectPropertyAction = useCallback(
    (fieldName) => (value) => {
      dispatch(changeObjectPropertyAction(REDUX_APP_PROPERTY, fieldName, value))
    },
    [dispatch]
  )

  const formatSIN = useCallback(
    (value) => {
      const sin = value.trim().replace(/\s+/g, '')
      dispatch(changeObjectPropertyAction(REDUX_APP_PROPERTY, 'socialInsuranceNumber', sin))
    },
    [dispatch]
  )

  const displaySIN = useMemo(() => {
    let sin = socialInsuranceNumber.substr(0, 3)
    if (socialInsuranceNumber.length > 3) {
      sin += ` ${socialInsuranceNumber.substr(3, 3)}`
    }
    if (socialInsuranceNumber.length > 6) {
      sin += ` ${socialInsuranceNumber.substr(6, 3)}`
    }
    return sin
  }, [socialInsuranceNumber])

  // const handleChangeObjectPayDebtOnBehalf = useCallback((fieldName, value) => {
  //     dispatch(changeObjectPropertyAction(REDUX_APP_PROPERTY, 'payee', { ...payDebtOnBehalf, [fieldName]: value }))
  // }, [])

  const validateFullName = useCallback((fullName) => {
    const fullNameErrors = validateFullNameErrors(fullName)
    setFormErrors((prevFormErrors) => ({ ...prevFormErrors, fullName: fullNameErrors }))
  }, [])

  const validateDateOfBirth = useCallback(
    (dob) => {
      let dateOfBirthErrors = ''
      try {
        let dayRegex = RegExp(/^\d{2}$/)
        let monthRegex = RegExp(/^\d{2}$/)
        let yearRegex = RegExp(/^\d{4}$/)
        if (
          dayRegex.test(dateOfBirthDayEl.current.value) &&
          monthRegex.test(dateOfBirthMonthEl.current.value) &&
          yearRegex.test(dateOfBirthYearEl.current.value)
        ) {
          let inputDate = `${dateOfBirthYearEl.current.value}-${dateOfBirthMonthEl.current.value}-${dateOfBirthDayEl.current.value}`
          dateOfBirthErrors = validateDateOfBirthErrors(inputDate)
        } else {
          dateOfBirthErrors = 'contact_information.date_of_birth_error'
        }

        setFormErrors((prevFormErrors) => ({ ...prevFormErrors, dateOfBirth: dateOfBirthErrors }))
      } catch (e) {
        console.log(e)
        setIsError(true)
      }
    },
    [setIsError]
  )

  const validatePhoneNumber = useCallback((phoneNumber) => {
    const phoneNumberErrors = validatePhoneNumberErrors(phoneNumber)
    setFormErrors((prevFormErrors) => ({ ...prevFormErrors, phoneNumber: phoneNumberErrors }))
  }, [])

  const validatePayeePhoneNumber = useCallback((phoneNumber) => {
    const phoneNumberErrors = validatePhoneNumberErrors(phoneNumber)
    setPayeeFormErrors((prevFormErrors) => ({ ...prevFormErrors, payeePhoneNumber: phoneNumberErrors }))
  }, [])

  const validatePayeeFullName = useCallback((fullName) => {
    const fullNameErrors = validateFullNameErrors(fullName)
    setPayeeFormErrors((prevFormErrors) => ({ ...prevFormErrors, payeeFullName: fullNameErrors }))
  }, [])

  const validateEmail = useCallback((email) => {
    const newFormErrors = validateEmailErrors(email)
    setFormErrors((prevFormErrors) => ({ ...prevFormErrors, email: newFormErrors }))
  }, [])

  const validatePayeeEmail = useCallback((email) => {
    const newFormErrors = validateEmailErrors(email)
    setPayeeFormErrors((prevFormErrors) => ({ ...prevFormErrors, payeeEmail: newFormErrors }))
  }, [])

  const validateSocialInsuranceNumber = useCallback((sin) => {
    const sinErrors = validateSocialInsuranceNumberErrors(sin.trim().replace(/\s+/g, ''))
    setFormErrors((prevFormErrors) => ({ ...prevFormErrors, socialInsuranceNumber: sinErrors }))
  }, [])

  const validateFields = useCallback(() => {
    const newFormErrors = { ...formErrors }
    newFormErrors.fullName = validateFullNameErrors(fullName)
    newFormErrors.dateOfBirth = validateDateOfBirthErrors(dateOfBirth)
    newFormErrors.phoneNumber = validatePhoneNumberErrors(phoneNumber)
    newFormErrors.email = validateEmailErrors(email)
    newFormErrors.socialInsuranceNumber = validateSocialInsuranceNumberErrors(socialInsuranceNumber)

    const newPayeeFormErrors = { ...payeeFormErrors }
    if (payeeContactInformation) {
      newPayeeFormErrors.payeePhoneNumber = validatePhoneNumberErrors(payeeContactInformation.phoneNumber)
      newPayeeFormErrors.payeeFullName = validateFullNameErrors(payeeContactInformation.fullName)
      newPayeeFormErrors.payeeEmail = validateEmailErrors(payeeContactInformation.email)
    } else {
      newPayeeFormErrors.payeePhoneNumber = ''
      newPayeeFormErrors.payeeFullName = ''
      newPayeeFormErrors.payeeEmail = ''
    }

    // Update form error states
    setFormErrors({ ...newFormErrors })
    setPayeeFormErrors({ ...newPayeeFormErrors })

    if (payeeContactInformation) {
      const isPayeeValid = Object.values(newPayeeFormErrors).every((val) => !val)
      const isPayeeFormClean = Object.values(newPayeeFormErrors).some((val) => val === '')
      if (isPayeeFormClean || !isPayeeValid) {
        return false
      }
    }

    const isValid = Object.values(newFormErrors).every((val) => !val)
    const isFormClean = Object.values(newFormErrors).some((val) => val === '')

    const newDebtInfo = {
      fullName,
      dateOfBirth,
      socialInsuranceNumber,
      phoneNumber,
      email,
    }

    return !isFormClean && isValid ? newDebtInfo : false
  }, [formErrors, payeeFormErrors, fullName, dateOfBirth, phoneNumber, socialInsuranceNumber, email, payeeContactInformation])

  useEffect(() => {
    setValidateForm(() => validateFields)
  }, [setValidateForm, validateFields])

  const handleRadio = (e) => {
    let field = e.currentTarget.value
    dispatch(setPayDebtOnBehalf(field === 'payDebtOnBehalf'))
  }

  return (
    <>
      <div className="row">
        <div className="col-xs-12 col-md-8">
          {/* Full Name */}
          <FieldInput
            id="fullName"
            name="fullName"
            type="text"
            customClasses={'fw-20'}
            aria-describedby="full-name"
            autoComplete="off"
            maxLength="150"
            label={t('contact_information.full_name')}
            t={t}
            disabled={!!editDebtInfoId}
            value={fullName}
            validate={validateFullName}
            errorMsg={formErrors.fullName}
            isValid={formErrors.fullName === '' ? '' : !formErrors.fullName}
            setIsError={setIsError}
            changeObjectPropertyAction={handleChangeObjectPropertyAction('fullName')}
          />

          <DateOfBirth
            validateDateOfBirth={validateDateOfBirth}
            dateOfBirthError={formErrors.dateOfBirth}
            dateOfBirthValid={formErrors.dateOfBirth === '' ? '' : !formErrors.dateOfBirth}
            dateOfBirthDayEl={dateOfBirthDayEl}
            dateOfBirthMonthEl={dateOfBirthMonthEl}
            dateOfBirthYearEl={dateOfBirthYearEl}
            setIsError={setIsError}
          />

          {/* Social Insurance Number */}
          <FieldInput
            id="SocialInsuranceNumber"
            name="SocialInsuranceNumber"
            type="text"
            customClasses={'fw-10'}
            aria-describedby="social-insurance-number"
            autoComplete="off"
            maxLength="11"
            label={t('contact_information.social_insurance_number')}
            t={t}
            value={displaySIN}
            pattern={'[0-9]*'}
            validate={validateSocialInsuranceNumber}
            errorMsg={formErrors.socialInsuranceNumber}
            isValid={formErrors.socialInsuranceNumber === '' ? '' : !formErrors.socialInsuranceNumber}
            setIsError={setIsError}
            changeObjectPropertyAction={formatSIN}
          />

          <PhoneNumber
            validatePhoneNumber={validatePhoneNumber}
            phoneNumberError={formErrors.phoneNumber}
            phoneNumberValid={formErrors.phoneNumber === '' ? '' : !formErrors.phoneNumber}
            setIsError={setIsError}
            reduxAppProperty={REDUX_APP_PROPERTY}
            changeObjectPropertyAction={(_, val) => dispatch(changeObjectPropertyAction(REDUX_APP_PROPERTY, 'phoneNumber', val))}
          />
          <Email
            validateEmail={validateEmail}
            emailError={formErrors.email}
            emailValid={formErrors.email === '' ? '' : !formErrors.email}
            setIsError={setIsError}
            reduxAppProperty={REDUX_APP_PROPERTY}
            changeObjectPropertyAction={(_, val) => dispatch(changeObjectPropertyAction(REDUX_APP_PROPERTY, 'email', val))}
          />
        </div>
      </div>
      <div className="row" style={{ marginTop: '10px' }}>
        <div className="col-xs-12 col-md-8">
          <div className="ns-radios__item">
            <input
              className="ns-radios__input"
              type="radio"
              id={t('contact_information.pay_a_debt_for_self')}
              value="payDebtForSelf"
              onChange={handleRadio}
              checked={!payeeContactInformation}
            />
            <label
              className="ns-label ns-radios__label"
              htmlFor={t('contact_information.pay_a_debt_for_self')}
              key={t('contact_information.pay_a_debt_for_self')}
            >
              <span className="ns-label-span">{t('contact_information.pay_a_debt_for_self')}</span>
            </label>
          </div>
          <div className="ns-radios__item">
            <input
              className="ns-radios__input"
              type="radio"
              id={t('contact_information.pay_debt_on_behalf')}
              value="payDebtOnBehalf"
              onChange={handleRadio}
              checked={!!payeeContactInformation}
            />
            <label
              className="ns-label ns-radios__label"
              htmlFor={t('contact_information.pay_debt_on_behalf')}
              key={t('contact_information.pay_debt_on_behalf')}
            >
              <span className="ns-label-span">{t('contact_information.pay_debt_on_behalf')}</span>
            </label>
          </div>
        </div>
      </div>
      {payeeContactInformation && (
        <Fade in={!!payeeContactInformation} duration={300}>
          <div className={styles['other_contact_div']}>
            {(!payeeFormErrors.payeeFullName || !payeeFormErrors.payeeEmail || !payeeFormErrors.payeePhoneNumber) && (
              <div className={styles['vertical_line']}></div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ marginLeft: '10px' }}>{t('contact_information.info_for_person_paying_debt')}</h3>
              <div className="col-xs-12 col-md-8">
                {!!payeeContactInformation && (
                  <>
                    <FullName
                      validateFullName={validatePayeeFullName}
                      fullNameError={payeeFormErrors.payeeFullName}
                      fullNameValid={payeeFormErrors.payeeFullName === '' ? '' : !payeeFormErrors.payeeFullName}
                      setIsError={setIsError}
                      reduxAppProperty={PAY_ON_BEHALF_PROPERTY}
                      changeObjectPropertyAction={(_, val) => dispatch(changeObjectPropertyAction(PAY_ON_BEHALF_PROPERTY, 'fullName', val))}
                      maxLength={50}
                    />
                    <Email
                      validateEmail={validatePayeeEmail}
                      emailError={payeeFormErrors.payeeEmail}
                      emailValid={payeeFormErrors.payeeEmail === '' ? '' : !payeeFormErrors.payeeEmail}
                      setIsError={setIsError}
                      reduxAppProperty={PAY_ON_BEHALF_PROPERTY}
                      changeObjectPropertyAction={(_, val) => dispatch(changeObjectPropertyAction(PAY_ON_BEHALF_PROPERTY, 'email', val))}
                    />
                    <PhoneNumber
                      validatePhoneNumber={validatePayeePhoneNumber}
                      phoneNumberError={payeeFormErrors.payeePhoneNumber}
                      phoneNumberValid={payeeFormErrors.payeePhoneNumber === '' ? '' : !payeeFormErrors.payeePhoneNumber}
                      setIsError={setIsError}
                      reduxAppProperty={PAY_ON_BEHALF_PROPERTY}
                      changeObjectPropertyAction={(_, val) =>
                        dispatch(changeObjectPropertyAction(PAY_ON_BEHALF_PROPERTY, 'phoneNumber', val))
                      }
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </Fade>
      )}
    </>
  )
}

export default withTranslation('common')(ContactInformationForm)
